import { useEffect, useState } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../../services/apiClient';

const emptyLocation = {
  type: 'city',
  name: '',
  parent_id: '',
  is_active: true,
};

const AdminLocations = ({ token }) => {
  const [locations, setLocations] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [form, setForm] = useState(emptyLocation);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/admin/locations', token);
      setLocations(res || []);
    } catch (err) {
      setError(err.message || 'Failed to load locations');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setEditingId(null);
    setForm(emptyLocation);
  };

  const save = async () => {
    if (!form.name || !form.type) return;
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      parent_id: form.parent_id || null,
    };
    try {
      if (editingId) {
        await apiPut(`/api/admin/locations/${editingId}`, payload, token);
      } else {
        await apiPost('/api/admin/locations', payload, token);
      }
      await load();
      reset();
    } catch (err) {
      setError(err.message || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const removeLocation = async (id) => {
    if (!id) return;
    if (!window.confirm('Delete this location?')) return;
    setSaving(true);
    setError('');
    try {
      await apiDelete(`/api/admin/locations/${id}`, token);
      await load();
      if (editingId === id) reset();
    } catch (err) {
      setError(err.message || 'Failed to delete location');
    } finally {
      setSaving(false);
    }
  };

  const parentOptions = (type) => {
    const map = { state: 'country', city: 'state' };
    const parentType = map[type];
    if (!parentType) return [];
    return locations.filter((l) => l.type === parentType && l.is_active);
  };

  const visibleLocations = filterType
    ? locations.filter((l) => l.type === filterType)
    : locations;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Properties</p>
          <h1 className="admin-page-title text-xl font-semibold text-slate-900">Locations (Country/State/City)</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">Filter</label>
          <select
            className="form-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All</option>
            <option value="country">Country</option>
            <option value="state">State</option>
            <option value="city">City</option>
          </select>
        </div>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="admin-card bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label text-sm">Type</label>
            <select
              className="form-select"
              value={form.type}
              onChange={(e) => setForm((v) => ({ ...v, type: e.target.value }))}
            >
              <option value="country">Country</option>
              <option value="state">State</option>
              <option value="city">City</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label text-sm">Name</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))}
              placeholder="Goa"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label text-sm">Parent</label>
            <select
              className="form-select"
              value={form.parent_id}
              onChange={(e) => setForm((v) => ({ ...v, parent_id: e.target.value }))}
            >
              <option value="">-- None --</option>
              {parentOptions(form.type).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.type})
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3 d-flex align-items-center gap-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="loc-active"
              checked={form.is_active}
              onChange={(e) => setForm((v) => ({ ...v, is_active: e.target.checked }))}
            />
            <label htmlFor="loc-active" className="form-check-label text-sm">
              Active
            </label>
          </div>
          <div className="col-12 d-flex gap-2">
            <button className="btn btn-primary" disabled={saving} onClick={save}>
              {saving ? 'Saving...' : editingId ? 'Update location' : 'Add location'}
            </button>
            {editingId && (
              <button className="btn btn-outline-secondary" onClick={reset}>
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Parent</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && !visibleLocations.length && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No locations yet
                  </td>
                </tr>
              )}
              {visibleLocations.map((l) => (
                <tr key={l.id}>
                  <td className="fw-semibold">{l.name}</td>
                  <td className="text-muted small text-uppercase">{l.type}</td>
                  <td className="text-muted small">
                    {l.parent_name || '--'}
                  </td>
                  <td>
                    <span className={`badge ${l.is_active ? 'bg-success-subtle text-success' : 'bg-secondary'}`}>
                      {l.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-end">
                    <div className="d-inline-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          setEditingId(l.id);
                          setForm({
                            type: l.type,
                            name: l.name || '',
                            parent_id: l.parent_id ? String(l.parent_id) : '',
                            is_active: l.is_active ?? true,
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeLocation(l.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminLocations;
